/**
 * A function to create a log object for the 'searchSubmit' event (evid: 300)
 *
 * Event description: Should be sent after visitor submitted search in Search Result by clicking 'Enter'
 *
 * @param {searchSubmitParams} params - The parameters for this event
 * @property {boolean} isDemo - True|False
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchSubmitParams>} LogObject<searchSubmitParams>
 */
export function searchSubmit(params) {
    return { evid: 300, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'loadingStarted' event (evid: 301)
 *
 * Event description: Should be sent after visitor has been redirected to the search results page and rendering process for widget started
 *
 * @param {loadingStartedParams} params - The parameters for this event
 * @return {LogObject<loadingStartedParams>} LogObject<loadingStartedParams>
 */
export function loadingStarted(params) {
    return { evid: 301, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'loadingFinished' event (evid: 302)
 *
 * Event description: Event should be sent when widget rendering finished
 *
 * @param {loadingFinishedParams} params - The parameters for this event
 * @property {boolean} success - True|False
 * @property {string} error - error message
 * @property {number} loadingDuration - time  between loading.started and loading.finished,ms
 * @return {LogObject<loadingFinishedParams>} LogObject<loadingFinishedParams>
 */
export function loadingFinished(params) {
    return { evid: 302, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'searchRequestStarted' event (evid: 303)
 *
 * Event description: Should be sent after visitor has been submitted search and request was sent to server
 *
 * @param {searchRequestStartedParams} params - The parameters for this event
 * @property {string} orderingDirection - Ordering direction, one of:  ASC, DESC.
 * @property {string} orderingFieldName - Ordering field name
 * @property {boolean} isDemo - True|False
 * @property {string} documentType - pages|products|blog
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchRequestStartedParams>} LogObject<searchRequestStartedParams>
 */
export function searchRequestStarted(params) {
    return { evid: 303, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'searchRequestFinished' event (evid: 304)
 *
 * Event description: Should be sent after visitor has been submitted search and request result was received
 *
 * @param {searchRequestFinishedParams} params - The parameters for this event
 * @property {boolean} success - true|false
 * @property {number} resultCount - result count for specific document type
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} orderingDirection - Ordering direction, one of: ASC, DESC.
 * @property {string} documentIds - {"public/stores/products": ["id1", "id2", ...], "public/blog/posts": ["id1", "id2", ...], ...}
 * @property {string} orderingFieldName - Ordering field name
 * @property {boolean} isDemo - True|False
 * @property {string} selectedFacets - json with all selected facets, in case no facets chosen this is null!
 * @property {string} error - error message
 * @property {string} origin - why the result page was loaded?  tab_change/editor_search_bar/result_page_search_bar/direct_visit/sort/page_change/facets
 * @property {string} documentType - pages|products|blog
 * @property {string} availableFacets - json with all availabel facets
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @property {number} loadingDuration - time between request.sent and request.resultReceived
 * @return {LogObject<searchRequestFinishedParams>} LogObject<searchRequestFinishedParams>
 */
export function searchRequestFinished(params) {
    return { evid: 304, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'documentClick' event (evid: 305)
 *
 * Event description: Event should be sent on visitor's click on Search Result
 *
 * @param {documentClickParams} params - The parameters for this event
 * @property {string} clickOrigin - image/title/add_to_cart
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} pageUrl - URL
 * @property {string} resultClicked - the result title the user clicked on
 * @property {boolean} isDemo - True|False
 * @property {string} documentType - all|pages|products|blogPosts
 * @property {number} searchIndex - index of the clicked item from search result set (search_index starts from 0)
 * @property {string} target - past was page title since 03-21 it is the search term  the user wrote
 * @property {string} documentId - Unique id for each vertical's document
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<documentClickParams>} LogObject<documentClickParams>
 */
export function documentClick(params) {
    return { evid: 305, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'documentTypeChange' event (evid: 306)
 *
 * Event description: Event should be sent on result tab click (one of All/Pages/Products/BlogPost) or viewAll
 *
 * @param {documentTypeChangeParams} params - The parameters for this event
 * @property {string} source - ViewAll|Tab
 * @property {string} tabName - the tab the user clicked in the search results page, in case user navigates between tabs
 * @property {boolean} isDemo - True|False
 * @property {string} target - all|pages|products|blogPosts
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<documentTypeChangeParams>} LogObject<documentTypeChangeParams>
 */
export function documentTypeChange(params) {
    return { evid: 306, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'searchResultsClickResetFacets' event (evid: 307)
 *
 * Event description: user clicked clear filters in result page
 *
 * @param {searchResultsClickResetFacetsParams} params - The parameters for this event
 * @property {string} clickOrigin - empty_result_page
 * @property {boolean} isDemo - True|False
 * @property {string} selectedFacets - json with all selected facets, in case no facets chosen this is null!
 * @property {string} availableFacets - json with all availabel facets
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchResultsClickResetFacetsParams>} LogObject<searchResultsClickResetFacetsParams>
 */
export function searchResultsClickResetFacets(params) {
    return { evid: 307, src: 99, endpoint: 'wix-search-widget', params: params };
}
/**
 * A function to create a log object for the 'wixSearchBarClickX' event (evid: 309)
 *
 * Event description: Should be sent after visitor submitted search in Search Result by clicking 'Enter'
 *
 * @param {wixSearchBarClickXParams} params - The parameters for this event
 * @property {boolean} isDemo - True|False
 * @return {LogObject<wixSearchBarClickXParams>} LogObject<wixSearchBarClickXParams>
 */
export function wixSearchBarClickX(params) {
    return { evid: 309, src: 99, endpoint: 'wix-search-widget', params: params };
}
//# sourceMappingURL=index.js.map